import { useTransition, animated } from 'react-spring';
import React__default, { useEffect, useState } from 'react';
import { SubTitle } from './components/Typography.es.js';
import { SuccessColored, InforOutlined, DeleteCircleColored, CloseOutlined } from '@teko/ds-icon';
import { styled } from '@teko/styled';
import { u as uid } from './utils-f1412689.js';

const toastIcon$1 = {
    success: React__default.createElement(SuccessColored, { size: 30 }),
    info: React__default.createElement(InforOutlined, { size: 30 }),
    warning: React__default.createElement(InforOutlined, { size: 30 }),
    error: React__default.createElement(DeleteCircleColored, { size: 30 }),
};
const Toast$1 = ({ type, title, message, onClose, duration = 5000, closeable = true, icon, }) => {
    useEffect(() => {
        let handle;
        if (duration) {
            handle = setTimeout(() => onClose(), duration);
        }
        return () => {
            if (handle) {
                clearTimeout(handle);
            }
        };
    }, []);
    return (React__default.createElement(StyledToast$1, null,
        closeable && (React__default.createElement(CloseOutlined, { className: "teko-toast-close", size: 24, color: "#000000", onClick: onClose })),
        React__default.createElement("div", { className: "teko-toast-content" },
            (icon || type) && (React__default.createElement("div", { className: "teko-toast-icon" }, icon ? icon : type && toastIcon$1[type])),
            React__default.createElement("div", null,
                title && (React__default.createElement("div", { className: "teko-toast-header" }, typeof title === 'string' ? (React__default.createElement(SubTitle, { level: 2, className: "teko-toast-title" }, title)) : (React__default.createElement("div", { className: "teko-toast-title" }, title)))),
                React__default.createElement("div", { className: "teko-toast-message" }, message)))));
};
const StyledToast$1 = styled.div `
  width: 384px;
  max-width: calc(100vw - 24px * 2);
  position: relative;
  background: white;
  .teko-toast-icon {
    margin-right: 20px;
  }
  .teko-toast-close {
    position: absolute;
    z-index: 1000;
    top: 16px;
    right: 16px;
    cursor: pointer;
  }
  .teko-toast-content {
    position: relative;
    display: flex;
    padding: 16px 24px;
    margin-bottom: 16px;
    border-radius: 8px;
    box-shadow: 0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%),
      0 9px 28px 8px rgb(0 0 0 / 5%);
    &.teko-toast-header {
      margin-bottom: 8px;
    }
  }
`;

const ToastContextDesktop = React__default.createContext({});
const toastPlacement = [
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
];
const ToastProviderDesktop = ({ children, }) => {
    const [toastPlacementState, setToastPlacementState] = useState({
        topLeft: [],
        topRight: [],
        bottomLeft: [],
        bottomRight: [],
    });
    const transitionConfig = { tension: 125, friction: 20, precision: 0.1 };
    const toastTransitions = {
        topRight: useTransition(toastPlacementState.topRight, {
            from: { opacity: 0, transform: 'translate(40px, 0px)' },
            enter: { opacity: 1, transform: 'translate(0px, 0px)' },
            leave: { opacity: 0, transform: 'translate(0px, -10px)' },
            config: transitionConfig,
        }),
        bottomRight: useTransition(toastPlacementState.bottomRight, {
            from: { opacity: 0, transform: 'translate(40px, 0px)' },
            enter: { opacity: 1, transform: 'translate(0px, 0px)' },
            leave: { opacity: 0, transform: 'translate(0px, 10px)' },
            config: transitionConfig,
        }),
        topLeft: useTransition(toastPlacementState.topLeft, {
            from: { opacity: 0, transform: 'translate(-40px, 0px)' },
            enter: { opacity: 1, transform: 'translate(0px, 0px)' },
            leave: { opacity: 0, transform: 'translate(0px, -10px)' },
            config: transitionConfig,
        }),
        bottomLeft: useTransition(toastPlacementState.bottomLeft, {
            from: { opacity: 0, transform: 'translate(-40px, 0px)' },
            enter: { opacity: 1, transform: 'translate(0px, 0px)' },
            leave: { opacity: 0, transform: 'translate(0px, 10px)' },
            config: transitionConfig,
        }),
    };
    const open = (props) => {
        const toastPlacement = props.placement || 'topRight';
        if (toastPlacement === 'topLeft' || toastPlacement === 'topRight') {
            setToastPlacementState((state) => (Object.assign(Object.assign({}, state), { [toastPlacement]: [...state[toastPlacement], Object.assign(Object.assign({}, props), { id: uid() })] })));
            return;
        }
        setToastPlacementState((state) => (Object.assign(Object.assign({}, state), { [toastPlacement]: [Object.assign(Object.assign({}, props), { id: uid() }), ...state[toastPlacement]] })));
    };
    const onClose = (placement, item) => {
        setToastPlacementState((state) => (Object.assign(Object.assign({}, state), { [placement]: state[placement].filter((e) => e.id !== item.id) })));
    };
    const toast = {
        open,
    };
    ['success', 'info', 'error', 'warning'].forEach((type) => {
        toast[type] = (props) => open(Object.assign({ type }, props));
    });
    return (React__default.createElement(ToastContextDesktop.Provider, { value: toast },
        children,
        toastPlacement.map((place) => {
            var _a;
            if (!((_a = toastPlacementState[place]) === null || _a === void 0 ? void 0 : _a.length))
                return;
            const placementCls = `teko-toast teko-toast-${place}`;
            return (React__default.createElement(ToastPositionGroup, { key: place, className: placementCls }, toastTransitions[place]((style, item) => {
                return (React__default.createElement(animated.div, { key: item.id, style: style },
                    React__default.createElement(Toast$1, Object.assign({}, item, { onClose: () => onClose(place, item) }))));
            })));
        })));
};
const ToastPositionGroup = styled.div `
  &.teko-toast {
    position: fixed;
    z-index: 1070;
    &.teko-toast-topRight {
      right: 24px;
      top: 24px;
    }
    &.teko-toast-topLeft {
      left: 24px;
      top: 24px;
    }
    &.teko-toast-bottomLeft {
      left: 24px;
      bottom: 24px;
    }
    &.teko-toast-bottomRight {
      right: 24px;
      bottom: 24px;
    }
  }
`;

const toastIcon = {
    success: React__default.createElement(SuccessColored, { size: 30 }),
    info: React__default.createElement(InforOutlined, { size: 30 }),
    warning: React__default.createElement(InforOutlined, { size: 30 }),
    error: React__default.createElement(DeleteCircleColored, { size: 30 }),
};
const Toast = ({ message, icon, placement = 'middle', type, duration = 5000, onClose, }) => {
    useEffect(() => {
        let handle;
        if (duration) {
            handle = setTimeout(() => onClose(), duration);
        }
        return () => {
            if (handle) {
                clearTimeout(handle);
            }
        };
    }, []);
    const positionClass = `teko-mobile-toast teko-mobile-toast-${placement}`;
    return (React__default.createElement(StyledToast, { className: positionClass },
        (icon || type) && (React__default.createElement("div", { className: "teko-mobile-toast-icon" }, icon ? icon : type && toastIcon[type])),
        React__default.createElement("div", { className: "teko-mobile-toast-message" }, message)));
};
const StyledToast = styled.div `
  &.teko-mobile-toast {
    display: flex;
    z-index: 1070;
    position: fixed;
    left: 50%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-width: 70%;
    padding: 8px 16px;
    color: #fff;
    font-size: 14px;
    line-height: 20px;
    white-space: pre-wrap;
    text-align: center;
    word-break: break-word;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 8px;
    transform: translate3d(-50%, -50%, 0);
    &.teko-mobile-toast-top {
      top: 20%;
    }
    &.teko-mobile-toast-middle {
      top: 50%;
    }
    &.teko-mobile-toast-bottom {
      top: 80%;
    }
    .teko-mobile-toast-icon {
      margin-bottom: 4px;
    }
  }
`;

const ToastContextMobile = React__default.createContext({});
const ToastProviderMobile = ({ children, }) => {
    const [toastProps, setToastProps] = useState(null);
    const open = (props) => {
        if (toastProps && JSON.stringify(toastProps) === JSON.stringify(props)) {
            return;
        }
        setToastProps(props);
    };
    const toast = {
        open,
    };
    ['success', 'info', 'error', 'warning'].forEach((type) => {
        toast[type] = (props) => open(Object.assign({ type }, props));
    });
    const onClose = () => setToastProps(null);
    const transitions = useTransition(toastProps, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { tension: 125, friction: 20, precision: 0.1 },
    });
    return (React__default.createElement(ToastContextMobile.Provider, { value: toast },
        children,
        toastProps &&
            transitions((style, item) => item && (React__default.createElement(animated.div, { style: style },
                React__default.createElement(Toast, Object.assign({}, item, { onClose: onClose })))))));
};

export { ToastContextDesktop as T, ToastContextMobile as a, ToastProviderDesktop as b, ToastProviderMobile as c, toastPlacement as t };
